import React, { createContext, useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { storeScrollPosition } from '../utils/sectionBasedScrollManager';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);

  // Global resize handler - only triggers on width changes
  useEffect(() => {
    let resizeTimeout;
    let lastWidth = window.innerWidth;

    const handleResize = () => {
      const currentWidth = window.innerWidth;

      // Only trigger if width has changed
      if (currentWidth !== lastWidth) {
        // Store scroll position before reload
        storeScrollPosition();

        // Show loading screen
        setIsLoading(true);

        // Clear any existing timeout
        clearTimeout(resizeTimeout);

        // Reload page after a short delay to show loading screen
        resizeTimeout = setTimeout(() => {
          window.location.reload();
        }, 250);

        // Update the last width
        lastWidth = currentWidth;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  const showLoading = () => setIsLoading(true);
  const hideLoading = () => setIsLoading(false);

  const value = {
    isLoading,
    showLoading,
    hideLoading
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {/* Global Loading Screen - Rendered to document body */}
      {isLoading &&
        createPortal(
          <div className="loading-screen">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>,
          document.body
        )}
    </LoadingContext.Provider>
  );
};
